import 'react-native-gesture-handler';
import React, { useEffect, useMemo, useState } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { Provider as PaperProvider } from 'react-native-paper';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { store } from './src/redux/store';
import { buildPaperTheme } from './src/assets/theme';
import { AppThemeProvider, useAppTheme } from './src/theme/ThemeContext';
import AppNavigator from './src/navigation/AppNavigator';
import { ActivityIndicator, Image, Platform, StatusBar, View } from 'react-native';
import { clearAuth, setMustChangePassword, setProfile, setSession } from './src/redux/slices/authSlice';
import { authService } from './src/services/authService';
import { setApiAuthHandlers } from './src/services/api';

const favicon = require('./assets/favicon.ico');

type FaviconLink = { rel: string; type: string; href: string; parentNode: { removeChild: (node: FaviconLink) => void } };
type WebDocument = {
  head: {
    querySelectorAll: (selector: string) => ArrayLike<FaviconLink>;
    appendChild: (node: FaviconLink) => void;
  };
  createElement: (tag: string) => FaviconLink;
};

function resolveBundledImageUri(source: unknown): string | undefined {
  if (typeof source === 'string' && source.length > 0) return source;
  if (source && typeof source === 'object') {
    const record = source as { uri?: unknown; default?: unknown };
    if (typeof record.uri === 'string' && record.uri.length > 0) return record.uri;
    if (typeof record.default === 'string' && record.default.length > 0) return record.default;
  }
  const resolveAssetSource = (
    Image as { resolveAssetSource?: (asset: unknown) => { uri?: string } | undefined }
  ).resolveAssetSource;
  if (typeof resolveAssetSource !== 'function') return undefined;
  return resolveAssetSource(source)?.uri;
}

function applyBrowserFavicon() {
  if (Platform.OS !== 'web') return;
  const webDocument = (globalThis as { document?: WebDocument }).document;
  const href = resolveBundledImageUri(favicon);
  if (!webDocument || !href) return;

  const existing = Array.from(
    webDocument.head.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']"),
  );
  const [link, ...duplicates] = existing;
  duplicates.forEach((duplicate) => duplicate.parentNode.removeChild(duplicate));

  const icon = link ?? webDocument.createElement('link');
  icon.rel = 'icon';
  icon.type = 'image/x-icon';
  icon.href = href;
  if (!link) webDocument.head.appendChild(icon);
}

function AppInit() {
  const [ready, setReady] = useState(false);
  const { colors } = useAppTheme();

  useEffect(() => {
    applyBrowserFavicon();
    setApiAuthHandlers({
      onUnauthorized: () => { store.dispatch(clearAuth()); },
      onPasswordChangeRequired: () => { store.dispatch(setMustChangePassword(true)); },
    });

    (async () => {
      try {
        const session = await authService.getStoredSession();
        if (session.token) {
          store.dispatch(setSession(session));
          try {
            const profile = await authService.me();
            store.dispatch(setProfile(profile));
          } catch (error: any) {
            if (error?.response?.status === 401) {
              await authService.logout();
              store.dispatch(clearAuth());
            }
          }
        }
      } finally {
        setReady(true);
      }
    })();
  }, []);

  if (!ready) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background }}>
        <ActivityIndicator color={colors.primary} />
      </View>
    );
  }

  return <AppNavigator />;
}

function ThemedApp() {
  const { colors } = useAppTheme();
  const paperTheme = useMemo(() => buildPaperTheme(colors), [colors]);

  return (
    <PaperProvider theme={paperTheme}>
      <StatusBar
        barStyle={colors.mode === 'dark' ? 'light-content' : 'dark-content'}
        backgroundColor={colors.background}
      />
      <AppInit />
    </PaperProvider>
  );
}

export default function App() {
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <ReduxProvider store={store}>
          <AppThemeProvider>
            <ThemedApp />
          </AppThemeProvider>
        </ReduxProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}
